import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import {
    getProductListDataView,
    getProductListDataViewProperty,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductListFiltersAccordionSection, {
    ProductListFilterAccordionSectionStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListFilterAccordionSection";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    expandByDefault = "expandByDefault",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.expandByDefault]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        wishListFacetsRaw: getProductListDataViewProperty(state, "properties")?.wishListFacets,
        products: getProductListDataView(state).value,
    };
};

const mapDispatchToProps = {
    addProductFilters,
    removeProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export const productListWishListFiltersStyles: ProductListFilterAccordionSectionStyles = {};

const countProductsInWishlists = (wishlists: any[], productsWithWishlists: any[], currentProducts: any[]) => {
    const wishlistCounts = new Map<string, number>();

    // Initialize counts for all wishlists
    wishlists.forEach(wishlist => {
        wishlistCounts.set(wishlist.id, 0);
    });

    // Get all product IDs from current products
    const currentProductIds = new Set(currentProducts.map(product => product.id));

    // Count products for each wishlist, only if they exist in current products
    productsWithWishlists.forEach(product => {
        // Only count if this product exists in current products
        if (currentProductIds.has(product.productId)) {
            product.wishlists?.forEach((wishlist: any) => {
                const currentCount = wishlistCounts.get(wishlist.id) || 0;
                wishlistCounts.set(wishlist.id, currentCount + 1);
            });
        }
    });

    return wishlistCounts;
};

const ProductListBrandFilters: FC<Props> = ({ wishListFacetsRaw, addProductFilters, removeProductFilters, fields }) => {
    // TODO ISC-11787 - make showMoreLimit configurable
    const showMoreLimit = 10;

    const wishListFacets = JSON.parse(wishListFacetsRaw || "[]").map((data: any) => ({
        count: data.count,
        id: data.id,
        name: data.name,
        selected: data.selected,
    })) as FacetModel[];

    if (!wishListFacets?.length) {
        return null;
    }

    const onChangeFacet = (facet: FacetModel) => {
        if (facet.selected) {
            removeProductFilters({ wishlistIds: [facet.id] } as any);
        } else {
            addProductFilters({ wishlistIds: [facet.id] } as any);
        }
    };

    return (
        <ProductListFiltersAccordionSection
            title={translate("Wish List")}
            facets={wishListFacets}
            onChangeFacet={onChangeFacet}
            showMoreLimit={showMoreLimit}
            expandByDefault={fields.expandByDefault}
            extendedStyles={productListWishListFiltersStyles}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListBrandFilters),
    definition: {
        group: "Product List",
        displayName: "Wish List Filters",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.expandByDefault,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
